export interface HilfeArticle {
  slug: string
  category: string
  title: string
  excerpt: string
  body: string
}

export const HILFE_ARTICLES: HilfeArticle[] = [
  {
    slug: 'konto-anlegen',
    category: 'Erste Schritte',
    title: 'Konto anlegen und erstes Restaurant einrichten',
    excerpt: 'In 10 Minuten von Null zu live: Restaurant anlegen, Öffnungszeiten setzen, Tische erstellen.',
    body: `## In 10 Minuten startklar

1. **Konto erstellen**: Klicken Sie oben rechts auf "Anmelden" oder direkt auf [/signup](/signup). Geben Sie E-Mail, Passwort, Restaurantname und Adresse ein.
2. **Onboarding**: Sie werden direkt durch die wichtigsten Einstellungen geführt: Öffnungszeiten, Reservierungseinstellungen, erste Tische.
3. **Speisekarte**: Unter "Bestellungen → Speisekarte" können Sie Kategorien und Gerichte mit Preisen, Beschreibungen und Allergenen hinzufügen.
4. **Widget einbinden**: Im Bereich "Onboarding → Widget-Integration" finden Sie einen Code-Schnipsel, den Sie in Ihre Website kopieren — fertig.

### Tipp
Sie können später jederzeit Räume und Tische hinzufügen, die Speisekarte erweitern und Mitarbeiter-Zugänge anlegen. Nichts ist in Stein gemeißelt.

### Häufige Stolpersteine
- **Bestätigungs-E-Mail nicht angekommen?** Schauen Sie im Spam-Ordner. Ansonsten schreiben Sie uns über das [Kontaktformular](/service).
- **Slug bereits vergeben?** Rezaro hängt automatisch eine Nummer an — Sie können den Slug später unter "Einstellungen → Website" anpassen.`,
  },
  {
    slug: 'widget-einbauen',
    category: 'Erste Schritte',
    title: 'Reservierungs-Widget auf Ihrer Website einbauen',
    excerpt: 'Code kopieren und auf Ihrer eigenen Website einbinden — in 3 Schritten.',
    body: `## Reservierungs-Widget einbinden

### Variante 1: Iframe (universell)

Im Admin-Panel unter **Onboarding → Widget-Integration** finden Sie einen fertigen Iframe-Code:

\`\`\`html
<iframe
  src="https://rezaro.de/IHR-RESTAURANT/widget"
  width="100%"
  height="640"
  style="border:0;border-radius:12px;"
  title="Reservierung"
></iframe>
\`\`\`

Kopieren Sie den Code in den HTML-Editor Ihrer Website (WordPress, Wix, Squarespace, Webflow oder direktes HTML).

### Variante 2: Direkter Link

Falls Sie kein Iframe einbinden möchten — z. B. weil Sie einen Button "Jetzt reservieren" anbieten — verwenden Sie einfach:

\`\`\`
https://rezaro.de/IHR-RESTAURANT/widget
\`\`\`

### Variante 3: QR-Code

Für Tischaufsteller oder Visitenkarten generieren wir Ihnen einen QR-Code unter **Onboarding → Widget-Integration → QR-Code herunterladen**.

### Tipp: Mobile First
Das Widget ist responsiv — testen Sie es auf dem Handy, da 70 % der Reservierungen mobil getätigt werden.`,
  },
  {
    slug: 'no-show-erinnerung',
    category: 'Reservierungen',
    title: 'No-Shows minimieren mit Erinnerungen und Anzahlungen',
    excerpt: 'Aktivieren Sie automatische E-Mail-Erinnerungen 24h vorher — und nutzen Sie Anzahlungen für Großgruppen.',
    body: `## So senken Sie Ihre No-Show-Quote auf unter 5 %

Studien zeigen: ohne Erinnerung kommen 15–25 % der Reservierungen nicht. Mit Erinnerung sinkt diese Zahl auf 3–8 %.

### Schritt 1: Automatische Erinnerung aktivieren
Gehen Sie zu **Einstellungen → Reservierungen** und aktivieren Sie:
- E-Mail-Erinnerung 24 Stunden vor dem Termin
- (Optional) E-Mail-Erinnerung 2 Stunden vor dem Termin

Der Gast erhält eine freundliche Erinnerung mit der Möglichkeit, die Reservierung mit einem Klick zu bestätigen oder abzusagen.

### Schritt 2: Anzahlung für sensible Slots
Bei Großgruppen (8+ Personen) oder besonderen Tagen (Valentinstag, Silvester) lohnt sich eine Anzahlung von €10–€20 pro Person.

So aktivieren Sie es:
1. **Einstellungen → Reservierungen → Anzahlung**
2. Definieren Sie Schwellenwerte (z. B. ab 8 Personen oder ab 19:00)
3. Setzen Sie den Betrag fest

Gäste, die anzahlen, kommen nachweislich zu 95 % erscheinen.

### Schritt 3: Wiederholte No-Shows automatisch sperren
Rezaro markiert Gäste mit 3+ No-Shows automatisch als gesperrt. Sie sehen den Status in der Gäste-Detailseite. Sie können Gäste jederzeit manuell entsperren.`,
  },
  {
    slug: 'qr-code-tisch',
    category: 'Bestellungen',
    title: 'QR-Code-Bestellung am Tisch einrichten',
    excerpt: 'Gäste scannen, bestellen und zahlen — ohne dass Sie zum Tisch müssen.',
    body: `## QR-Code-Bestellung in 5 Schritten

### Voraussetzung
- Pro-Plan oder höher
- Speisekarte angelegt
- Mindestens ein Raum mit Tischen

### Setup

1. **Tische benennen**: Unter **Einstellungen → Tische** vergeben Sie eindeutige Namen (z. B. T1, T2, Terrasse-3).
2. **QR-Codes generieren**: Im selben Bereich finden Sie pro Tisch einen "QR-Code drucken"-Button. Sie können einen Tischaufsteller oder einen Aufkleber bedrucken.
3. **Speisekarten-Layout prüfen**: Stellen Sie sicher, dass alle Gerichte ein Bild und eine kurze Beschreibung haben — das erhöht die Bestellquote.
4. **Zahlungsmethoden aktivieren**: Unter **Einstellungen → Bestellungen** schalten Sie Bar, Karte am Tisch oder direkte Online-Zahlung (Stripe) ein.
5. **Bon-Drucker anbinden**: Über das Webhook-System können Sie eigene Bon-Drucker oder externe Druck-Services per HTTP-Event anbinden.

### Tipp: Trinkgeld-Slider
Im Checkout-Schritt können Gäste ein Trinkgeld geben. Die Voreinstellung ist 0/5/10/15 % — passen Sie sie unter **Einstellungen → Bestellungen** an.`,
  },
  {
    slug: 'speisekarte-pflegen',
    category: 'Speisekarte',
    title: 'Speisekarte pflegen: Kategorien, Allergene, Verfügbarkeit',
    excerpt: 'So bleibt Ihre digitale Speisekarte aktuell — und steigert die Bestellquote.',
    body: `## Eine Speisekarte, die verkauft

### Kategorien sinnvoll strukturieren
Maximal 6 Hauptkategorien. Beispiel:
- Vorspeisen
- Hauptgerichte
- Pizza
- Pasta
- Desserts
- Getränke

### Pro Gericht: das Wesentliche
- **Foto** (querformatig, 4:3, mindestens 1200 px breit)
- **Titel** (kurz, 3–6 Wörter)
- **Beschreibung** (1–2 Sätze, sinnliche Worte: "knusprig", "hausgemacht", "ofenfrisch")
- **Preis** (mit Komma, z. B. 14,90)
- **Allergene** (Pflicht in DE — Auswahl aus AB-Liste)

### Allergene
Rezaro kennt die deutsche Pflichtliste (Gluten, Eier, Krebstiere, Fisch, Erdnüsse, Soja, Milch, Schalenfrüchte, Sellerie, Senf, Sesam, Schwefeldioxid, Lupinen, Weichtiere). Wählen Sie pro Gericht alle zutreffenden aus — ein Klick.

### Verfügbarkeit ein-/ausschalten
Ist ein Gericht ausverkauft? Klicken Sie auf den Schalter rechts neben dem Gericht. Es wird ausgegraut, aber nicht gelöscht — am nächsten Tag mit einem Klick wieder aktiv.

### Tipp: Beliebte Gerichte hervorheben
Markieren Sie 2–3 Gerichte als "Beliebt" oder "Empfehlung des Hauses". Die Bestellquote auf markierte Gerichte ist nachweislich 30 % höher.`,
  },
  {
    slug: 'gaeste-verwalten',
    category: 'Gäste',
    title: 'Gäste-Datenbank effektiv nutzen',
    excerpt: 'Stammgäste markieren, Notizen hinterlegen, gezielte Marketing-Kampagnen versenden.',
    body: `## Aus Daten Stammgäste machen

### Was sammelt Rezaro automatisch?
Bei jeder Reservierung oder Bestellung wird ein Gast-Eintrag angelegt (Name, E-Mail, Telefon). Bei wiederkehrenden Gästen wird der \`visit_count\` automatisch erhöht.

### Was tun mit der Liste?

**1. VIP-Segment nutzen**
Im Bereich **Gäste → VIP (5+ Besuche)** sehen Sie alle Stammgäste. Senden Sie ihnen einmal pro Quartal eine persönliche E-Mail (Newsletter-Funktion).

**2. Notizen hinterlegen**
Klicken Sie auf einen Gast und notieren Sie:
- Allergien (Erdnussallergie, Laktoseintoleranz)
- Vorlieben (Tisch am Fenster, ohne Eis im Drink)
- Besondere Anlässe (Geburtstag, Jahrestag)

Das Service-Team sieht diese Notizen automatisch in der Reservierung.

**3. Inaktive Gäste reaktivieren**
Im Filter **Inaktiv (90+ Tage)** finden Sie Gäste, die länger nicht da waren. Senden Sie eine Win-Back-Kampagne mit einem Anreiz (z. B. "Welcome back" — Aperitif aufs Haus).

**4. No-Show-Liste**
Gäste mit 2+ No-Shows werden mit einem amber Badge markiert. Bei der nächsten Reservierung wird "Anzahlung empfehlen" eingeblendet.`,
  },
  {
    slug: 'plan-aendern',
    category: 'Konto & Abrechnung',
    title: 'Plan ändern: Upgrade, Downgrade oder Pause',
    excerpt: 'Sie sind nicht an einen Plan gebunden — wechseln Sie jederzeit.',
    body: `## Wie funktioniert das Abo?

### Pläne
- **Starter** — €29/Monat, bis 100 Reservierungen, Tischplan bis 10 Tische
- **Pro** — €69/Monat, alle Kernfunktionen, geeignet für 90 % aller Restaurants
- **Premium** — €119/Monat, Multi-Standort (bis 5), Webhooks, dedizierter Ansprechpartner

### Upgrade
Im Admin-Panel **Konto & Plan** klicken Sie auf den gewünschten Plan und werden zur Stripe-Zahlungsseite geleitet. Der neue Plan ist sofort aktiv.

### Downgrade
Genauso einfach — am Ende des aktuellen Abrechnungszeitraums springt Ihr Plan auf das niedrigere Niveau. Funktionen, die der niedrigere Plan nicht abdeckt, werden ausgeblendet (Daten bleiben erhalten).

### Pause
Wir bieten eine Saisonpause für 2 Monate pro Jahr (z. B. für Saisonbetriebe). Sie zahlen in dieser Zeit nur €5/Monat zur Datensicherung. Schreiben Sie uns über das [Kontaktformular](/service).

### Kündigung
Jederzeit zum Monatsende, ohne Frist. Daten werden 90 Tage aufbewahrt und auf Wunsch als JSON/CSV exportiert.`,
  },
  {
    slug: 'rechnung-herunterladen',
    category: 'Konto & Abrechnung',
    title: 'Rechnung herunterladen und USt-IdNr eintragen',
    excerpt: 'Ihre PDF-Rechnung ist DATEV-kompatibel und enthält Ihre USt-IdNr für Reverse-Charge.',
    body: `## So finden und nutzen Sie Ihre Rechnungen

### USt-IdNr eintragen (B2B)
Damit Sie Reverse-Charge-Rechnungen ohne deutsche MwSt erhalten, hinterlegen Sie Ihre USt-IdNr unter **Konto & Plan → Rechnungsinformationen**. Wir prüfen die Nummer automatisch über VIES.

### PDF-Rechnung herunterladen
Im Bereich **Konto & Plan → Rechnungen** finden Sie alle bisherigen PDF-Rechnungen. Klick auf den Download-Button → fertig.

### DATEV-Export _(in Kürze)_
Ab Q3 2026 bieten wir einen DATEV-konformen CSV-Export, den Ihr Steuerberater direkt einlesen kann.

### E-Rechnung (XRechnung) _(geplant)_
Ab 01.01.2027 sind elektronische B2B-Rechnungen in Deutschland verpflichtend. Wir generieren XRechnung automatisch parallel zur PDF.

### Bei Fragen
Schreiben Sie uns über das [Kontaktformular](/service) oder direkt an info@rezaro.de.`,
  },
  {
    slug: 'tischplan-einrichten',
    category: 'Reservierungen',
    title: 'Tischplan einrichten: Räume, Tische, Kapazitäten',
    excerpt: 'Räume anlegen, Tische ziehen, Min/Max-Kapazitäten setzen — der visuelle Tischplan in 5 Minuten.',
    body: `## Tischplan visuell verwalten

### Räume anlegen
Gehen Sie zu **Einstellungen → Tische**. Klicken Sie auf "Raum hinzufügen". Geben Sie einen Namen (z. B. "Innenbereich", "Terrasse", "Privatraum") und optional eine Beschreibung ein.

### Tische ziehen und positionieren
Im Raum-Editor können Sie Tische per Klick hinzufügen — runde, eckige, rechteckige. Per Drag-and-Drop positionieren, mit den Anfassern resizen.

### Min/Max-Kapazität
Für jeden Tisch setzen Sie:
- **min**: kleinste sinnvolle Belegung (z. B. 1 für 2er-Tisch, 4 für 6er-Tisch)
- **max**: physische Höchstbelegung
Rezaro wählt automatisch den effizientesten Tisch für jede Buchung.

### Vereinbare Tische (Kombinationen)
Mehrere kleine Tische können zu einer größeren Gruppe kombiniert werden. Im Tisch-Editor "Kombinierbar mit" einstellen.

### Aussenbereich saisonal
Markieren Sie "nur im Sommer aktiv" — Rezaro deaktiviert die Tische außerhalb der Saison automatisch.`,
  },
  {
    slug: 'schichten-konfigurieren',
    category: 'Reservierungen',
    title: 'Schichten konfigurieren: Lunch, Dinner, Brunch',
    excerpt: 'Mehrere Schichten pro Tag, unterschiedliche Verweildauern pro Schicht — so geht es.',
    body: `## Schichten clever planen

### Mehrere Schichten pro Tag
Standardmäßig laufen Lunch (12-15 Uhr) und Dinner (18-23 Uhr) als separate Schichten mit unterschiedlichen Eigenschaften.

### Verweildauer je Schicht
Lunch-Gäste bleiben durchschnittlich 45-60 min, Dinner-Gäste 90-120 min. Rezaro berücksichtigt das beim automatischen Tischvergeben.

Unter **Einstellungen → Reservierungen → Schichten**:
- Lunch: Slot-Länge 90 min, Default-Verweildauer 60 min
- Dinner: Slot-Länge 120 min, Default-Verweildauer 105 min

### Wochenend-Brunch
Für Samstag/Sonntag fügen Sie eine dritte Schicht hinzu (10:00–14:00), mit Slot-Länge 90 min und 6-7 Schicht-Turnover.

### Geschlossene Tage / Urlaub
Unter **Einstellungen → Voreinstellungen → Schliesstage** verwalten Sie einzelne Tage (Renovation, Privatfeier) oder ganze Zeiträume (Urlaub).`,
  },
  {
    slug: 'waitlist-verwalten',
    category: 'Reservierungen',
    title: 'Warteliste: Gäste auch bei voller Kapazität halten',
    excerpt: 'Wenn alle Tische belegt sind, sammelt Rezaro die Warteliste — und benachrichtigt automatisch bei Storno.',
    body: `## Warteliste aktivieren

### Was ist die Warteliste?
Sobald alle Slots eines Datums belegt sind, kann der Gast statt einer Buchung auf die Warteliste. Storniert jemand, geht automatisch eine Mail an die erste Person.

### Aktivieren
**Einstellungen → Reservierungen → Warteliste aktivieren** → ein-/ausschalten.

### Wie sieht das für den Gast aus?
Im Widget erscheint nach "Keine freien Slots verfügbar" der Button "Auf die Warteliste". Gast trägt sich mit Name, Telefon, E-Mail ein.

### Auto-Promote
Sobald jemand storniert, wird die erste Person auf der Warteliste angemailt: "Ein Tisch ist frei geworden — klicken Sie hier zum Bestätigen."

### Verwaltung
Unter **Reservierungen → Warteliste** sehen Sie alle Einträge, manuell promoten oder löschen möglich.

### Tipp
Die Warteliste konvertiert ca. 30-40 % der Anfragen. Wer einmal "Vorrang" hatte, kommt eher zurück.`,
  },
  {
    slug: 'stammgast-tags',
    category: 'Gäste',
    title: 'Stammgäste markieren: VIP, Allergie, Anlass',
    excerpt: 'Tags pro Gast — VIP-Erkennung im Tischplan, Allergie-Notiz im Service, Lieblingstisch automatisch.',
    body: `## Mit Tags den Service personalisieren

### Tags anlegen
Im Gast-Profil (**Gäste → Gast auswählen → Tags**) fügen Sie kurze Marker hinzu:
- **VIP** — wichtige Stammgäste, hervorgehoben im Tischplan
- **Glutenfrei**, **Vegan**, **Nuss-Allergie** — Service-Hinweise
- **Hochzeitstag**, **Geburtstag** — Anlässe für besonderen Empfang

### Im Tischplan sichtbar
VIP-Tische bekommen einen goldenen Rand, Allergie-Notizen erscheinen unter dem Gastnamen.

### Auto-Tags
Rezaro vergibt automatisch:
- **Stammgast** ab 3 Besuchen
- **Risiko** nach 2 No-Shows
- **Blockiert** nach 4 No-Shows (manuell aufhebbar)

### Tag-Filter in der Liste
Unter **Gäste** können Sie alle VIPs anzeigen, alle Allergiker, alle Geburtstagsgäste — für Marketing-Aktionen filtern.

### Beispiel-Workflow
Hochzeitstag-Tag aktivieren → 14 Tage vor dem Datum bekommt der Gast eine personalisierte Einladung mit speziellem Menüvorschlag.`,
  },
  {
    slug: 'ical-kalender-abonnieren',
    category: 'Integration',
    title: 'Reservierungen im Apple/Google Kalender abonnieren',
    excerpt: 'iCal-Feed-URL kopieren, in Kalender-App einfügen — Reservierungen erscheinen automatisch.',
    body: `## Reservierungen im Kalender sehen

### iCal-Feed URL holen
Im Bereich **Einstellungen → Integration → Kalender** finden Sie Ihre persönliche iCal-URL:

\`\`\`
https://rezaro.de/api/bookings/ical?token=IHRTOKEN
\`\`\`

Diese URL ist personalisiert und sollte nicht öffentlich geteilt werden.

### Apple Kalender (Mac/iPhone)
- **Mac**: Kalender → Ablage → Neues Kalender-Abonnement → URL einfügen
- **iPhone**: Einstellungen → Kalender → Accounts → Neuer Account → Andere → Kalender-Abo hinzufügen

### Google Kalender
1. Google Kalender öffnen
2. Links bei "Weitere Kalender" auf "+" klicken
3. "Per URL hinzufügen" wählen
4. iCal-URL einfügen → "Kalender hinzufügen"

Aktualisierung erfolgt alle 4-12 Stunden je nach Anbieter.

### Outlook
Outlook → Kalender → Kalender öffnen → Aus dem Internet → URL einfügen.

### URL erneuern
Bei Verdacht auf Leak: **Einstellungen → Integration → Kalender → URL erneuern** generiert einen neuen Token, alter wird ungültig.`,
  },
  {
    slug: 'push-benachrichtigungen',
    category: 'Mobile App',
    title: 'Push-Benachrichtigungen für neue Reservierungen',
    excerpt: 'Mobile App installieren, Berechtigung erteilen — Sie bekommen Push bei jeder neuen Buchung.',
    body: `## Push aktivieren

### Mobile App installieren
Auf iPhone (iOS 16.4+) oder Android (Chrome): rezaro.de öffnen → "Zum Home-Bildschirm hinzufügen". Es entsteht ein App-Icon, das wie eine echte App aussieht.

Alternativ: native App im App Store / Play Store (Q3 2026).

### Push-Berechtigung erteilen
Beim ersten App-Start fragt Rezaro nach Push-Berechtigung. **Erlauben** drücken.

Falls Sie abgelehnt haben: iOS Einstellungen → Rezaro → Mitteilungen → erlauben. Android: App-Einstellungen → Benachrichtigungen.

### Was wird benachrichtigt?
Unter **Einstellungen → Voreinstellungen → Benachrichtigungen** wählen Sie:
- Neue Reservierung
- Stornierung
- Verspätungsmeldung
- Walk-in (eingegeben durch Kollege)
- Neue Google-Bewertung

### Mehrere Geräte
Sie können auf mehreren Geräten gleichzeitig aktiv sein. Jedes Gerät bekommt Push.

### Stille Zeiten
Setzen Sie unter **Mitteilungen → Stille Zeiten** Slots, in denen keine Push kommt (z. B. 02:00–08:00).`,
  },
  {
    slug: 'bewertungen-google',
    category: 'Marketing',
    title: 'Google-Bewertungen automatisch sammeln',
    excerpt: 'Nach jedem Besuch automatisch Bewertungs-Link versenden — 3-27 Stunden nach Abreise, idealer Zeitpunkt.',
    body: `## Mehr 5-Sterne ohne Aufwand

### Google-Place-ID einrichten
**Einstellungen → Marketing → Google-Bewertungen** → "Place-ID suchen" → Restaurant aus Google Maps wählen.

### Trigger-Logik
Sobald eine Reservierung auf "Abgereist" gesetzt wird (manuell oder automatisch nach Schicht-Ende), startet ein Timer:
- 3 Stunden warten (Gast soll noch zu Hause sein, frische Erinnerung)
- E-Mail versenden mit personalisiertem Link zur Google-Bewertung

### E-Mail-Inhalt
- "Wie war Ihr Besuch?"
- 5-Sterne-Quickbuttons
- Bei 5 Sternen: weiterleiten zu Google
- Bei 1-4 Sternen: weiterleiten zum internen Bewertungsformular (Sie lernen, ohne dass es öffentlich wird)

### Idempotenz
Pro Buchung wird höchstens 1 Bewertungsanfrage versendet (Rezaro markiert die Buchung als "review_request_sent_at").

### Conversion-Rate
Übliche Conversion: 8-12 % der Gäste klicken durch und bewerten. Bei 100 Gästen pro Woche → ca. 8-12 neue Bewertungen.

### Texte anpassen
**Einstellungen → Marketing → Bewertungs-Mail Vorlage** — Betreff und Body editierbar.`,
  },
  {
    slug: 'csv-export-import',
    category: 'Daten',
    title: 'Gäste & Reservierungen als CSV exportieren / importieren',
    excerpt: 'Daten jederzeit als CSV oder JSON exportieren — auch für Migration von anderen Reservierungs-Anbietern.',
    body: `## Datenexport — Sie bleiben Herr Ihrer Daten

### Gäste-Export
**Gäste → Alle Gäste → Export-Button** lädt eine CSV mit allen Gästen herunter:
- Name, E-Mail, Telefon
- Buchungsanzahl, letzter Besuch
- Tags, Notizen
- Marketing-Consent-Status

### Reservierungs-Export
**Reservierungen → CSV-Export** mit allen Reservierungen eines Zeitraums (max. 12 Monate pro Export).

### Import (Migration)
Wenn Sie von einem anderen Reservierungs-Anbieter wechseln:
1. CSV bei altem Anbieter exportieren (sie sind verpflichtet, DSGVO)
2. **Konto → Datenmigration → CSV hochladen**
3. Spalten zuordnen
4. Vorschau prüfen, importieren

### Spalten-Format
Wir akzeptieren die gängigen Spalten-Namen: \`name\`, \`email\`, \`phone\`, \`notes\`, \`tags\`. Bei Abweichungen ordnen Sie manuell zu.

### DSGVO-Export
Wenn ein Gast Auskunft nach Art. 15 DSGVO verlangt, generieren Sie eine vollständige JSON-Datei mit allen Daten dieses Gastes unter **Gäste → Gast → "Daten exportieren (DSGVO)"**.

### Lösch-Antrag
Für Art. 17 (Recht auf Vergessen): **Gäste → Gast → "Komplett löschen"** — Buchungen werden anonymisiert (Statistiken bleiben), persönliche Daten weg.`,
  },
]

export function getArticleBySlug(slug: string): HilfeArticle | undefined {
  return HILFE_ARTICLES.find((a) => a.slug === slug)
}
